import { useEffect, useState } from "react";
import {
  Modal,
  Stack,
  TextInput,
  Select,
  Checkbox,
  Button,
  Group,
  Text,
  Divider,
  Radio,
  Alert,
  Loader,
  Center,
  ColorInput,
  NumberInput,
  Textarea,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getPrefixById,
  getPrefixTypes,
  listPrefixes,
  createPrefix,
  updatePrefix,
  AllowedBillingScenarioRequest,
} from "../../../services/prefixService";
import { useBillingScenarios } from "../../../hooks/admin/useBillingScenarios";
import {
  MAX_CASE_NUMBER_SEQUENCE,
  MIN_CASE_NUMBER_SEQUENCE,
  validateCaseNumberSequence,
  validateSequenceChangeReason,
} from "../../../utils/caseNumberSequence";

interface PrefixFormModalProps {
  opened: boolean;
  onClose: () => void;
  prefixId?: string;
  onSuccess: () => void;
}

interface FormState {
  code: string;
  prefixTypeId: string;
  color: string;
  fallbackPrefixId: string;
  allowedBillingScenarioIds: string[];
  defaultBillingScenarioId: string;
  nextSequence: number | string;
  sequenceChangeReason: string;
}

const DEFAULT_FORM: FormState = {
  code: "",
  prefixTypeId: "",
  color: "",
  fallbackPrefixId: "",
  allowedBillingScenarioIds: [],
  defaultBillingScenarioId: "",
  nextSequence: "",
  sequenceChangeReason: "",
};

export default function PrefixFormModal({
  opened,
  onClose,
  prefixId,
  onSuccess,
}: PrefixFormModalProps) {
  const isEdit = !!prefixId;
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [errors, setErrors] = useState<
    Partial<Record<keyof FormState, string>>
  >({});

  const { data: prefixDetail, isLoading: loadingDetail } = useQuery({
    queryKey: ["prefix-admin", prefixId],
    queryFn: () => getPrefixById(prefixId!, { includeSequence: true }),
    enabled: isEdit && opened,
    staleTime: 30_000,
  });

  const { data: prefixTypes = [] } = useQuery({
    queryKey: ["prefixTypes"],
    queryFn: getPrefixTypes,
    enabled: opened,
  });

  const { data: billingScenarios = [] } = useBillingScenarios();

  const { data: allPrefixesData } = useQuery({
    queryKey: ["prefixes", { includeInactive: false }],
    queryFn: () => listPrefixes({ includeInactive: false, limit: 100 }),
    enabled: opened,
  });

  const prefixTypeOptions = prefixTypes.map((t) => ({
    value: t.id,
    label: `${t.name} (${t.assignmentStrategy})`,
  }));

  const fallbackPrefixOptions = (allPrefixesData?.data ?? [])
    .filter((p) => p.id !== prefixId)
    .map((p) => ({ value: p.id, label: p.code }));

  // Populate form when editing
  useEffect(() => {
    if (isEdit && prefixDetail) {
      const checkedIds = prefixDetail.allowedBillingScenarios.map(
        (a) => a.billingScenarioId
      );
      const defaultId =
        prefixDetail.allowedBillingScenarios.find((a) => a.isDefault)
          ?.billingScenarioId ?? "";
      setForm({
        code: prefixDetail.code,
        prefixTypeId: prefixDetail.prefixTypeId ?? "",
        color: prefixDetail.color ?? "",
        fallbackPrefixId: prefixDetail.fallbackPrefixId ?? "",
        allowedBillingScenarioIds: checkedIds,
        defaultBillingScenarioId: defaultId,
        nextSequence: prefixDetail.sequence?.nextSequence ?? "",
        sequenceChangeReason: "",
      });
    } else if (!isEdit && opened) {
      setForm(DEFAULT_FORM);
    }
    setErrors({});
  }, [isEdit, prefixDetail, opened]);

  const createMutation = useMutation({
    mutationFn: createPrefix,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prefixes"] });
      onSuccess();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      dto,
    }: {
      id: string;
      dto: Parameters<typeof updatePrefix>[1];
    }) => updatePrefix(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prefixes"] });
      queryClient.invalidateQueries({ queryKey: ["prefix-admin", prefixId] });
      onSuccess();
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const validate = (): boolean => {
    const errs: Partial<Record<keyof FormState, string>> = {};
    if (!form.code.trim()) errs.code = "Code is required";
    if (!form.prefixTypeId) errs.prefixTypeId = "Prefix type is required";
    if (
      form.allowedBillingScenarioIds.length > 0 &&
      !form.defaultBillingScenarioId
    ) {
      errs.defaultBillingScenarioId =
        "A default billing scenario is required when allowed scenarios are specified";
    }
    const nextSequence = Number(form.nextSequence);
    const sequenceError = isEdit
      ? validateCaseNumberSequence(form.nextSequence)
      : null;
    if (sequenceError) errs.nextSequence = sequenceError;
    const reasonError = validateSequenceChangeReason(
      form.sequenceChangeReason,
      isEdit && nextSequence !== prefixDetail?.sequence?.nextSequence
    );
    if (reasonError) errs.sequenceChangeReason = reasonError;
    if (
      form.defaultBillingScenarioId &&
      !form.allowedBillingScenarioIds.includes(form.defaultBillingScenarioId)
    ) {
      errs.defaultBillingScenarioId =
        "Default billing scenario must be in the allowed scenarios list";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const buildAllowedBillingScenarios = (): AllowedBillingScenarioRequest[] =>
    form.allowedBillingScenarioIds.map((id) => ({
      billingScenarioId: id,
      isDefault: id === form.defaultBillingScenarioId,
    }));

  const handleSubmit = async () => {
    if (!validate()) return;
    const allowedBillingScenarios = buildAllowedBillingScenarios();

    if (isEdit && prefixId) {
      const sequenceChanged =
        Number(form.nextSequence) !== prefixDetail?.sequence?.nextSequence;
      await updateMutation.mutateAsync({
        id: prefixId,
        dto: {
          prefixTypeId: form.prefixTypeId || undefined,
          color: form.color || null,
          fallbackPrefixId: form.fallbackPrefixId || null,
          allowedBillingScenarios,
          ...(sequenceChanged && {
            nextSequence: Number(form.nextSequence),
            sequenceChangeReason: form.sequenceChangeReason.trim(),
          }),
        },
      });
    } else {
      await createMutation.mutateAsync({
        code: form.code,
        prefixTypeId: form.prefixTypeId,
        color: form.color || null,
        fallbackPrefixId: form.fallbackPrefixId || null,
        allowedBillingScenarios,
      });
    }
  };

  const toggleBillingScenario = (value: string) => {
    setForm((prev) => {
      const next = prev.allowedBillingScenarioIds.includes(value)
        ? prev.allowedBillingScenarioIds.filter((v) => v !== value)
        : [...prev.allowedBillingScenarioIds, value];
      return {
        ...prev,
        allowedBillingScenarioIds: next,
        defaultBillingScenarioId:
          prev.defaultBillingScenarioId === value && !next.includes(value)
            ? ""
            : prev.defaultBillingScenarioId,
      };
    });
  };

  const mutationError =
    createMutation.error?.message ?? updateMutation.error?.message;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? "Edit Prefix" : "Add Prefix"}
      size="lg"
      closeOnClickOutside={false}
    >
      {isEdit && loadingDetail ? (
        <Center p="xl">
          <Loader />
        </Center>
      ) : (
        <Stack gap="md">
          {mutationError && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              {mutationError}
            </Alert>
          )}

          <TextInput
            label="Code"
            placeholder="e.g. BG"
            value={form.code}
            onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
            disabled={isEdit}
            error={errors.code}
            required
          />

          <Select
            label="Prefix Type"
            data={prefixTypeOptions}
            value={form.prefixTypeId || null}
            onChange={(v) => setForm((p) => ({ ...p, prefixTypeId: v ?? "" }))}
            placeholder="Select prefix type"
            searchable
            error={errors.prefixTypeId}
            required
          />

          <Group gap="xl">
            <ColorInput
              label="Color"
              placeholder="e.g. #3B82F6"
              value={form.color}
              onChange={(v) => setForm((p) => ({ ...p, color: v }))}
              style={{ flex: 1 }}
              format="hex"
              swatches={[
                "#ef4444",
                "#f97316",
                "#eab308",
                "#22c55e",
                "#3b82f6",
                "#8b5cf6",
                "#ec4899",
              ]}
            />
            <Select
              label="Fallback Prefix"
              placeholder="No fallback"
              data={fallbackPrefixOptions}
              value={form.fallbackPrefixId || null}
              onChange={(v) =>
                setForm((p) => ({ ...p, fallbackPrefixId: v ?? "" }))
              }
              searchable
              clearable
              style={{ flex: 1 }}
              description="Used when insurance eligibility fails"
            />
          </Group>

          {isEdit && (
            <>
              <Divider label="Case Number Sequence" labelPosition="left" />
              <Alert color="blue">
                Set the next available number for this prefix. Numbers already
                assigned or reserved are skipped automatically.
              </Alert>
              <NumberInput
                label="Next sequence number"
                description={`Current next case number: ${
                  prefixDetail?.sequence?.nextCaseNumber ?? "—"
                }`}
                min={MIN_CASE_NUMBER_SEQUENCE}
                max={MAX_CASE_NUMBER_SEQUENCE}
                allowDecimal={false}
                value={form.nextSequence}
                onChange={(value) =>
                  setForm((current) => ({ ...current, nextSequence: value }))
                }
                error={errors.nextSequence}
                required
              />
              {Number(form.nextSequence) !==
                prefixDetail?.sequence?.nextSequence && (
                <Textarea
                  label="Reason for sequence change"
                  placeholder="Explain why the next sequence is being changed"
                  value={form.sequenceChangeReason}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      sequenceChangeReason: event.currentTarget.value,
                    }))
                  }
                  error={errors.sequenceChangeReason}
                  required
                  autosize
                  minRows={2}
                />
              )}
            </>
          )}

          <Divider label="Allowed Billing Scenarios" labelPosition="left" />

          <Stack gap="xs">
            {billingScenarios.map((scenario) => (
              <Group key={scenario.id} gap="md">
                <Checkbox
                  label={scenario.name}
                  checked={form.allowedBillingScenarioIds.includes(scenario.id)}
                  onChange={() => toggleBillingScenario(scenario.id)}
                />
                {form.allowedBillingScenarioIds.includes(scenario.id) && (
                  <Radio
                    size="xs"
                    label="Default"
                    checked={form.defaultBillingScenarioId === scenario.id}
                    onChange={() =>
                      setForm((p) => ({
                        ...p,
                        defaultBillingScenarioId: scenario.id,
                      }))
                    }
                    value={scenario.id}
                  />
                )}
              </Group>
            ))}
            {errors.defaultBillingScenarioId && (
              <Text size="xs" c="red">
                {errors.defaultBillingScenarioId}
              </Text>
            )}
          </Stack>

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={isPending}>
              {isEdit ? "Save Changes" : "Create Prefix"}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
